/**
 * DataTab - Data management tools
 * Extracted from DevPage.tsx for modularity
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, Button } from '@/components/shared';
import { db, Account, clearAllData, exportData, seedMockData } from '@services/db';
import { retagRecurringTransactions } from '@services/import';
import { devlog } from '@services/devlog';
import { RecentAdditionsPanel } from './RecentAdditionsPanel';

interface DataTabProps {
  onDataChange: () => void;
}

export function DataTab({ onDataChange }: DataTabProps) {
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [retagResult, setRetagResult] = useState<string | null>(null);

  // Balance fixer state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [balanceEdits, setBalanceEdits] = useState<Record<string, string>>({});
  const [applyingBalances, setApplyingBalances] = useState(false);
  const [balanceFixResult, setBalanceFixResult] = useState<string | null>(null);

  // Hidden accounts state
  const [hiddenAccounts, setHiddenAccounts] = useState<Account[]>([]);
  const [hiddenActionResult, setHiddenActionResult] = useState<string | null>(null);

  // Year deletion state
  const [availableYears, setAvailableYears] = useState<{ year: number; count: number }[]>([]);
  const [deletingYear, setDeletingYear] = useState<number | null>(null);
  const [yearDeleteResult, setYearDeleteResult] = useState<string | null>(null);
  const [confirmDeleteYear, setConfirmDeleteYear] = useState<number | null>(null);

  // Recalculate balances state
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<string | null>(null);

  // Load accounts for balance fixer AND hidden accounts
  const loadAccounts = useCallback(async () => {
    console.log('[DataTab] Loading accounts...');
    const allAccs = await db.accounts.toArray();
    const active = allAccs.filter(a => a.isActive !== false);
    const hidden = allAccs.filter(a => a.isActive === false);

    console.log('[DataTab] Active:', active.length, 'Hidden:', hidden.length);
    setAccounts(active);
    setHiddenAccounts(hidden);

    // Initialize balances
    const edits: Record<string, string> = {};
    active.forEach(a => {
      edits[a.id!] = a.balance.toFixed(2);
    });
    setBalanceEdits(edits);
  }, []);

  // Load available years from transactions
  const loadYears = useCallback(async () => {
    console.log('[DataTab] Loading transaction years...');
    const allTxns = await db.transactions.toArray();
    const yearCounts = new Map<number, number>();

    allTxns.forEach(tx => {
      const year = new Date(tx.date).getFullYear();
      yearCounts.set(year, (yearCounts.get(year) || 0) + 1);
    });

    // Sort years ascending
    const years = Array.from(yearCounts.entries())
      .map(([year, count]) => ({ year, count }))
      .sort((a, b) => a.year - b.year);

    console.log('[DataTab] Years found:', years);
    setAvailableYears(years);
  }, []);

  useEffect(() => {
    loadAccounts();
    loadYears();
  }, [loadAccounts, loadYears]);

  const handleBalanceChange = (id: string, value: string) => {
    setBalanceEdits(prev => ({ ...prev, [id]: value }));
  };

  const handleApplyBalances = async () => {
    setApplyingBalances(true);
    setBalanceFixResult(null);
    console.log('[BalanceFixer] Starting update, accounts:', accounts.length);
    try {
      let updated = 0;
      // Use transaction to ensure all updates commit
      await db.transaction('rw', db.accounts, async () => {
        for (const acc of accounts) {
          const newBalance = parseFloat(balanceEdits[acc.id!] || '0');
          if (!isNaN(newBalance)) {
            // Get fresh account from DB and update it
            const freshAcc = await db.accounts.get(acc.id!);
            if (freshAcc) {
              freshAcc.balance = newBalance;
              freshAcc.updatedAt = new Date();
              await db.accounts.put(freshAcc);
              console.log(`[BalanceFixer] ${freshAcc.name}: -> ${newBalance} [OK]`);
              updated++;
            }
          }
        }
      });
      console.log('[BalanceFixer] Transaction committed, updated:', updated);
      setBalanceFixResult(`[OK] Updated ${updated} account balances - refresh /accounts to see`);
      devlog.info('db', 'Balances fixed', { updated });
      onDataChange();
      // Reload accounts (re-splits active vs hidden)
      loadAccounts();
    } catch (err) {
      console.error('[BalanceFixer] Error:', err);
      setBalanceFixResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Balance fix failed', { error: String(err) });
    } finally {
      setApplyingBalances(false);
    }
  };

  const handleClear = useCallback(async () => {
    setClearing(true);
    try {
      await clearAllData();
      devlog.info('db', 'All data cleared - reloading page');
      // Force page reload to reset all React state
      window.location.reload();
    } catch (err) {
      devlog.error('db', 'Clear failed', { error: String(err) });
      setClearing(false);
    }
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const data = await exportData();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ninjacash-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      devlog.info('db', 'Data exported');
    } catch (err) {
      devlog.error('db', 'Export failed', { error: String(err) });
    } finally {
      setExporting(false);
    }
  }, []);

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    try {
      await seedMockData();
      devlog.info('db', 'Mock data seeded');
      onDataChange();
    } catch (err) {
      devlog.error('db', 'Seed failed', { error: String(err) });
    } finally {
      setSeeding(false);
    }
  }, [onDataChange]);

  const handleRetag = useCallback(async () => {
    setRetagging(true);
    setRetagResult(null);
    try {
      const result = await retagRecurringTransactions();
      setRetagResult(`[OK] Tagged ${result.updated} transactions with: ${result.patterns.join(', ')}`);
      devlog.info('db', 'Retagged recurring', result);
      onDataChange();
    } catch (err) {
      setRetagResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Retag failed', { error: String(err) });
    } finally {
      setRetagging(false);
    }
  }, [onDataChange]);

  // Handle unhide account
  const handleUnhideAccount = useCallback(async (id: string) => {
    try {
      const acc = await db.accounts.get(id);
      if (acc) {
        acc.isActive = true;
        acc.updatedAt = new Date();
        await db.accounts.put(acc);
        setHiddenActionResult(`[OK] Unhidden: ${acc.name}`);
        devlog.info('db', 'Account unhidden', { id, name: acc.name });
        loadAccounts();
        onDataChange();
      }
    } catch (err) {
      setHiddenActionResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Unhide failed', { error: String(err) });
    }
  }, [loadAccounts, onDataChange]);

  // Handle delete hidden account permanently
  const handleDeleteHiddenAccount = useCallback(async (id: string) => {
    try {
      const acc = await db.accounts.get(id);
      const name = acc?.name || 'Unknown';
      await db.accounts.delete(id);
      setHiddenActionResult(`[OK] Deleted: ${name}`);
      devlog.info('db', 'Account deleted', { id, name });
      loadAccounts();
      onDataChange();
    } catch (err) {
      setHiddenActionResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Delete failed', { error: String(err) });
    }
  }, [loadAccounts, onDataChange]);

  // Handle delete transactions by year
  const handleDeleteYear = useCallback(async (year: number) => {
    setDeletingYear(year);
    setYearDeleteResult(null);
    try {
      const allTxns = await db.transactions.toArray();
      const toDelete = allTxns.filter(tx => new Date(tx.date).getFullYear() === year);

      console.log(`[DataTab] Deleting ${toDelete.length} transactions from ${year}...`);

      // Delete in chunks for performance
      const ids = toDelete.map(tx => tx.id!);
      await db.transactions.bulkDelete(ids);

      setYearDeleteResult(`[OK] Deleted ${toDelete.length} transactions from ${year}`);
      devlog.info('db', 'Year transactions deleted', { year, count: toDelete.length });
      setConfirmDeleteYear(null);
      loadYears();
      onDataChange();
    } catch (err) {
      setYearDeleteResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Year delete failed', { error: String(err), year });
    } finally {
      setDeletingYear(null);
    }
  }, [loadYears, onDataChange]);

  // Handle recalculate all balances (manual tool)
  const handleRecalculateBalances = useCallback(async () => {
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const allAccounts = await db.accounts.toArray();
      const allTxns = await db.transactions.toArray();
      let updated = 0;

      await db.transaction('rw', db.accounts, async () => {
        for (const acc of allAccounts) {
          // Calculate balance from all transactions for this account
          let balance = 0;
          for (const tx of allTxns) {
            if (tx.accountId === acc.id) {
              if (tx.type === 'income') {
                balance += tx.amount;
              } else if (tx.type === 'expense') {
                balance -= tx.amount;
              } else if (tx.type === 'transfer') {
                balance -= tx.amount; // Outgoing transfer
              }
            }
            // Incoming transfers
            if (tx.type === 'transfer' && tx.toAccountId === acc.id) {
              balance += tx.amount;
            }
          }
          acc.balance = balance;
          acc.updatedAt = new Date();
          await db.accounts.put(acc);
          console.log(`[Recalc] ${acc.name}: ${balance.toFixed(2)}`);
          updated++;
        }
      });

      setRecalcResult(`[OK] Recalculated ${updated} account balances from transactions`);
      devlog.info('db', 'Balances recalculated', { updated });
      loadAccounts();
      onDataChange();
    } catch (err) {
      setRecalcResult(`[ERROR] ${String(err)}`);
      devlog.error('db', 'Recalculate failed', { error: String(err) });
    } finally {
      setRecalculating(false);
    }
  }, [loadAccounts, onDataChange]);

  return (
    <div className="tab-content">
      {/* Recent Additions - Ghost Transaction Detection - TOP PRIORITY */}
      <RecentAdditionsPanel onDataChange={onDataChange} />

      {/* Balance Fixer - First for visibility */}
      <Card>
        <CardHeader
          title="Fix Account Balances"
          subtitle="Set correct current balances for each account"
        />
        {accounts.length === 0 ? (
          <p className="empty-text">No accounts found.</p>
        ) : (
          <>
            <div className="balance-fixer-grid">
              {accounts.map((acc) => (
                <div key={acc.id} className="balance-fixer-row">
                  <div className="balance-fixer-name">
                    <strong>{acc.name}</strong>
                    {acc.fullName && <span className="subtext"> ({acc.fullName})</span>}
                  </div>
                  <div className="balance-fixer-input">
                    <span className="currency-prefix">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={balanceEdits[acc.id!] || '0'}
                      onChange={(e) => handleBalanceChange(acc.id!, e.target.value)}
                      className="balance-input"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="balance-fixer-actions">
              <Button
                variant="primary"
                onClick={handleApplyBalances}
                disabled={applyingBalances}
              >
                {applyingBalances ? 'Applying...' : 'Apply All Balances'}
              </Button>
            </div>
            {balanceFixResult && (
              <p className={`balance-result ${balanceFixResult.includes('[OK]') ? 'success' : 'error'}`}>
                {balanceFixResult}
              </p>
            )}
          </>
        )}
      </Card>

      {/* Hidden Accounts Management */}
      <Card>
        <CardHeader
          title="Hidden Accounts"
          subtitle={`${hiddenAccounts.length} hidden account${hiddenAccounts.length !== 1 ? 's' : ''}`}
        />
        {hiddenAccounts.length === 0 ? (
          <p className="empty-text">No hidden accounts.</p>
        ) : (
          <>
            <div className="hidden-accounts-list">
              {hiddenAccounts.map((acc) => (
                <div key={acc.id} className="hidden-account-row">
                  <div className="hidden-account-info">
                    <strong>{acc.name}</strong>
                    {acc.fullName && <span className="subtext"> ({acc.fullName})</span>}
                    <span className="hidden-account-balance">
                      {acc.balance.toLocaleString('en-US', {
                        style: 'currency',
                        currency: acc.currency || 'USD',
                      })}
                    </span>
                  </div>
                  <div className="hidden-account-actions">
                    <Button
                      variant="secondary"
                      onClick={() => handleUnhideAccount(acc.id!)}
                    >
                      Unhide
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteHiddenAccount(acc.id!)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {hiddenActionResult && (
              <p className={`balance-result ${hiddenActionResult.includes('[OK]') ? 'success' : 'error'}`}>
                {hiddenActionResult}
              </p>
            )}
          </>
        )}
      </Card>

      {/* Delete Transactions by Year */}
      <Card>
        <CardHeader
          title="Delete Transactions by Year"
          subtitle="Remove old/corrupted data year by year"
        />
        {availableYears.length === 0 ? (
          <p className="empty-text">No transactions found.</p>
        ) : (
          <>
            <div className="year-delete-grid">
              {availableYears.map(({ year, count }) => (
                <div key={year} className="year-delete-row">
                  <div className="year-info">
                    <strong>{year}</strong>
                    <span className="year-count">{count.toLocaleString()} transactions</span>
                  </div>
                  {confirmDeleteYear === year ? (
                    <div className="year-confirm-actions">
                      <span className="confirm-text">Delete {count} txns?</span>
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmDeleteYear(null)}
                        disabled={deletingYear !== null}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeleteYear(year)}
                        disabled={deletingYear !== null}
                      >
                        {deletingYear === year ? 'Deleting...' : 'Yes, Delete'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="danger"
                      onClick={() => setConfirmDeleteYear(year)}
                      disabled={deletingYear !== null}
                    >
                      Delete {year}
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {yearDeleteResult && (
              <p className={`balance-result ${yearDeleteResult.includes('[OK]') ? 'success' : 'error'}`}>
                {yearDeleteResult}
              </p>
            )}
          </>
        )}
      </Card>

      {/* Recalculate All Balances */}
      <Card>
        <CardHeader
          title="Recalculate All Balances"
          subtitle="Calculate balances from transactions (use after deleting old data)"
        />
        <p className="warning-text">
          Warning: This will recalculate ALL account balances based on transaction history.
          Only use after cleaning up corrupted data.
        </p>
        <Button
          variant="secondary"
          onClick={handleRecalculateBalances}
          disabled={recalculating}
        >
          {recalculating ? 'Recalculating...' : 'Recalculate Balances'}
        </Button>
        {recalcResult && (
          <p className={`balance-result ${recalcResult.includes('[OK]') ? 'success' : 'error'}`}>
            {recalcResult}
          </p>
        )}
      </Card>

      {/* Retag Recurring */}
      <Card>
        <CardHeader
          title="Retag Recurring Transactions"
          subtitle="Apply recurring patterns (Spotify, Netflix, DTE, Doordash, etc.) to all existing transactions"
        />
        <Button variant="secondary" onClick={handleRetag} disabled={retagging}>
          {retagging ? 'Retagging...' : 'Retag All Transactions'}
        </Button>
        {retagResult && (
          <p className={`balance-result ${retagResult.includes('[OK]') ? 'success' : 'error'}`}>
            {retagResult}
          </p>
        )}
      </Card>

      <Card>
        <CardHeader title="Export Data" subtitle="Download all your data as JSON" />
        <Button variant="secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export to JSON'}
        </Button>
      </Card>

      <Card>
        <CardHeader
          title="Seed Mock Data"
          subtitle="Add sample accounts and transactions for testing"
        />
        <Button variant="secondary" onClick={handleSeed} disabled={seeding}>
          {seeding ? 'Seeding...' : 'Add Mock Data'}
        </Button>
      </Card>

      <Card>
        <CardHeader
          title="Fix Categories"
          subtitle="Remove duplicates and ensure income categories exist"
        />
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              const all = await db.categories.toArray();
              const seen = new Map<string, string>(); // name -> id to keep
              const toDelete: string[] = [];

              // Find duplicates (keep first occurrence)
              // CASE-INSENSITIVE comparison to catch "Bill" vs "bill" duplicates
              for (const cat of all) {
                const key = `${cat.type}:${cat.name.toLowerCase().trim()}`;
                if (seen.has(key)) {
                  toDelete.push(cat.id!);
                } else {
                  seen.set(key, cat.id!);
                }
              }

              // Delete duplicates
              if (toDelete.length > 0) {
                await db.categories.bulkDelete(toDelete);
                console.log(`[Fix] Deleted ${toDelete.length} duplicate categories`);
              }

              // Ensure income categories exist
              const incomeCategories = ['Salary', 'Freelance', 'Investment', 'Gift', 'Other'];
              const existing = await db.categories.where('type').equals('income').toArray();
              const existingNames = new Set(existing.map(c => c.name));

              for (const name of incomeCategories) {
                if (!existingNames.has(name)) {
                  await db.categories.add({
                    id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    name,
                    type: 'income',
                    isActive: true,
                    color: '#2ECC71',
                  });
                  console.log(`[Fix] Added missing income category: ${name}`);
                }
              }

              // Remove icons from income categories
              const incomeWithIcons = await db.categories
                .where('type')
                .equals('income')
                .filter(c => !!c.icon)
                .toArray();
              for (const cat of incomeWithIcons) {
                await db.categories.update(cat.id!, { icon: undefined });
              }
              if (incomeWithIcons.length > 0) {
                console.log(`[Fix] Removed icons from ${incomeWithIcons.length} income categories`);
              }

              alert(`Fixed! Removed ${toDelete.length} duplicates, ensured ${incomeCategories.length} income categories exist.`);
              onDataChange();
            } catch (err) {
              console.error('[Fix] Error:', err);
              alert('Error fixing categories: ' + (err instanceof Error ? err.message : err));
            }
          }}
        >
          Fix Categories
        </Button>
      </Card>

      <Card className="danger-card">
        <CardHeader
          title="Clear All Data"
          subtitle="Permanently delete ALL accounts, transactions, and categories"
        />
        {!showClearConfirm ? (
          <Button variant="danger" onClick={() => setShowClearConfirm(true)}>
            Clear All Data
          </Button>
        ) : (
          <div className="confirm-section">
            <p className="confirm-text">
              Are you sure? This will delete everything and cannot be undone!
            </p>
            <div className="confirm-buttons">
              <Button variant="ghost" onClick={() => setShowClearConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleClear} disabled={clearing}>
                {clearing ? 'Clearing...' : 'Yes, Delete Everything'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
